import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MessageSquare, Phone, Mail, Clock, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { smsService, SmsSettingsDto, SmsLogDto, SmsLogPageDto } from "@/services/smsService";
import { chatbotService, ChatSettings } from "@/services/chatbotService";
import { useToast } from "@/hooks/use-toast";
import { useProjectSelection } from "@/contexts/ProjectSelectionContext";
import { useRoleAccess } from "@/hooks/useRoleAccess";

export default function Notification() {
  const { toast } = useToast();
  const { effectiveProjectId } = useProjectSelection();
  const { isAdmin } = useRoleAccess();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<SmsSettingsDto>({
    enableStaffSms: false,
    staffSmsGroupSize: -1,
    enableClientSms: false,
    largePartySmsTemplateFr: "",
    largePartySmsTemplateEn: "",
    enableStaffEmail: false,
    staffEmailGroupSize: -1,
    staffPhoneNumber: "",
    staffEmail: "",
    reminderTemplate: ""
  });
  const [chatSettings, setChatSettings] = useState<ChatSettings | null>(null);
  const [isLargePartySmsEnabled, setIsLargePartySmsEnabled] = useState(false);
  const [logs, setLogs] = useState<SmsLogDto[]>([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;
  const [sortConfig, setSortConfig] = useState<{ key: "sentAt" | "toNumber" | "content"; direction: "asc" | "desc"; }>({
    key: "sentAt",
    direction: "desc"
  });

  useEffect(() => {
    loadSettings();
    setCurrentPage(1);
    loadSmsLogs(1);
  }, [effectiveProjectId]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const [data, chatData] = await Promise.all([
        smsService.getSmsSettings(effectiveProjectId ?? undefined),
        chatbotService.getSettings(effectiveProjectId ?? undefined),
      ]);
      setSettings({
        ...data,
        staffSmsGroupSize: typeof data.staffSmsGroupSize === "number" ? data.staffSmsGroupSize : -1,
        staffEmailGroupSize: typeof data.staffEmailGroupSize === "number" ? data.staffEmailGroupSize : -1,
        largePartySmsTemplateFr: data.largePartySmsTemplateFr ?? "",
        largePartySmsTemplateEn: data.largePartySmsTemplateEn ?? "",
      });
      setChatSettings(chatData);
      setIsLargePartySmsEnabled((chatData?.maxGroupSizeHandling ?? "transfer") === "sms");
    } catch (error) {
      console.error("Failed to load SMS settings", error);
      toast({
        title: "Error",
        description: "Failed to load SMS settings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSmsLogs = async (page?: number) => {
    try {
      setLogsLoading(true);
      const targetPage = page ?? currentPage;
      const data = await smsService.getSmsLogs(effectiveProjectId ?? undefined, targetPage, pageSize);
      setLogs(data.items);
      setTotalPages(data.totalPages);
      setTotalCount(data.totalCount);
    } catch (error) {
      console.error("Failed to load SMS logs", error);
      toast({
        title: "Error",
        description: "Failed to load SMS history",
        variant: "destructive",
      });
    } finally {
      setLogsLoading(false);
    }
  };

  const goToPage = (page: number) => {
    setCurrentPage(page);
    loadSmsLogs(page);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const updatedSmsSettings = await smsService.updateSmsSettings(
        {
          ...settings,
          projectId: effectiveProjectId ?? null,
          enableClientSms: isLargePartySmsEnabled ? true : settings.enableClientSms,
        },
        effectiveProjectId ?? undefined
      );

      let targetChatSettings = chatSettings ?? await chatbotService.getSettings(effectiveProjectId ?? undefined);
      if (!targetChatSettings) {
        targetChatSettings = await chatbotService.createSettings(effectiveProjectId ?? undefined);
      }

      const updatedChatSettings = await chatbotService.updateSettings({
        ...targetChatSettings,
        projectId: effectiveProjectId ?? null,
        maxGroupSizeHandling: isLargePartySmsEnabled ? "sms" : "transfer",
      });

      setSettings(updatedSmsSettings);
      setChatSettings(updatedChatSettings);
      setIsLargePartySmsEnabled((updatedChatSettings.maxGroupSizeHandling ?? "transfer") === "sms");
      toast({
        title: "Success",
        description: "Notification settings saved successfully",
      });
    } catch (error) {
      console.error("Failed to save notification settings", error);
      toast({
        title: "Error",
        description: "Failed to save notification settings",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSort = (key: "sentAt" | "toNumber" | "content") => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: key === "sentAt" ? "desc" : "asc" };
    });
  };

  const sortedLogs = [...logs].sort((a, b) => {
    const { key, direction } = sortConfig;

    if (key === "sentAt") {
      const aTime = new Date(a.sentAt).getTime();
      const bTime = new Date(b.sentAt).getTime();
      return direction === "asc" ? aTime - bTime : bTime - aTime;
    }

    const aValue = (a[key] || "").toLowerCase();
    const bValue = (b[key] || "").toLowerCase();

    if (aValue < bValue) return direction === "asc" ? -1 : 1;
    if (aValue > bValue) return direction === "asc" ? 1 : -1;
    return 0;
  });

  const renderSortIcon = (key: "sentAt" | "toNumber" | "content") => {
    if (sortConfig.key !== key) return <ArrowUpDown className="h-4 w-4 text-muted-foreground" />;
    return sortConfig.direction === "asc" ? (
      <ArrowUp className="h-4 w-4" />
    ) : (
      <ArrowDown className="h-4 w-4" />
    );
  };

  const formatSentAt = (value: string) => {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    return date.toLocaleString();
  };

  const handleGroupSizeModeChange = (field: "staffSmsGroupSize" | "staffEmailGroupSize", mode: "all" | "threshold") => {
    setSettings((prev) => ({
      ...prev,
      [field]: mode === "all" ? -1 : Math.max(0, prev[field] ?? 0),
    }));
  };

  const handleGroupSizeValueChange = (field: "staffSmsGroupSize" | "staffEmailGroupSize", raw: string) => {
    const parsed = Number.parseInt(raw, 10);
    setSettings((prev) => ({
      ...prev,
      [field]: Number.isNaN(parsed) ? 0 : Math.max(0, parsed),
    }));
  };

  if (loading) {
    return <div className="p-8 text-center">Loading notification settings...</div>;
  }

  return (
    <div className="space-y-6 pb-20 lg:pb-6">
      {isAdmin && (
        <>
          <div className="mb-6">
            <h2 className="text-2xl font-semibold mb-2">Notification Settings</h2>
            <p className="text-muted-foreground">
              Configure notifications and customer reminders
            </p>
          </div>

          {/* Staff/Owner Notification SMS */}
          <Card className="p-6 card-hover">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Phone className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-lg font-semibold">Staff/Owner Notification</h3>
            </div>
            
            <div className="space-y-5">
              <div className="rounded-lg border p-4 space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-primary" />
                    <h4 className="font-medium">SMS Notifications</h4>
                  </div>
                  <div className="flex items-center gap-3">
                    <Label htmlFor="enable-staff-sms" className="text-sm text-muted-foreground">Enable</Label>
                    <Switch
                      id="enable-staff-sms"
                      checked={settings.enableStaffSms}
                      onCheckedChange={(checked) => setSettings({ ...settings, enableStaffSms: checked })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="staff-phone">Phone Number(s) for Summaries</Label>
                  <Input
                    id="staff-phone"
                    type="tel"
                    placeholder="+1 (555) 123-4567, +1 (555) 987-6543"
                    value={settings.staffPhoneNumber || ""}
                    onChange={(e) => setSettings({ ...settings, staffPhoneNumber: e.target.value })}
                  />
                  <p className="text-sm text-muted-foreground">Use comma-separated numbers to notify multiple recipients.</p>
                </div>

                <div className="space-y-2">
                  <Label>SMS Group Size Filter</Label>
                  <div className="grid gap-2 md:grid-cols-[1fr_140px]">
                    <Select
                      value={settings.staffSmsGroupSize < 0 ? "all" : "threshold"}
                      onValueChange={(value: "all" | "threshold") => handleGroupSizeModeChange("staffSmsGroupSize", value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All group sizes</SelectItem>
                        <SelectItem value="threshold">Only groups larger than</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      min={0}
                      value={settings.staffSmsGroupSize < 0 ? "" : settings.staffSmsGroupSize}
                      onChange={(e) => handleGroupSizeValueChange("staffSmsGroupSize", e.target.value)}
                      disabled={settings.staffSmsGroupSize < 0}
                      placeholder="8"
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">If set to 8, SMS is sent only when the group size is greater than 8.</p>
                </div>
              </div>

              <Separator />

              <div className="rounded-lg border p-4 space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-primary" />
                    <h4 className="font-medium">Email Notifications</h4>
                  </div>
                  <div className="flex items-center gap-3">
                    <Label htmlFor="enable-staff-email" className="text-sm text-muted-foreground">Enable</Label>
                    <Switch
                      id="enable-staff-email"
                      checked={settings.enableStaffEmail}
                      onCheckedChange={(checked) => setSettings({ ...settings, enableStaffEmail: checked })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="staff-email">Email(s) for Summaries</Label>
                  <Input
                    id="staff-email"
                    type="text"
                    placeholder="email@example.com, another@example.com"
                    value={settings.staffEmail || ""}
                    onChange={(e) => setSettings({ ...settings, staffEmail: e.target.value })}
                  />
                  <p className="text-sm text-muted-foreground">Use comma-separated emails to notify multiple recipients.</p>
                </div>

                <div className="space-y-2">
                  <Label>Email Group Size Filter</Label>
                  <div className="grid gap-2 md:grid-cols-[1fr_140px]">
                    <Select
                      value={settings.staffEmailGroupSize < 0 ? "all" : "threshold"}
                      onValueChange={(value: "all" | "threshold") => handleGroupSizeModeChange("staffEmailGroupSize", value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All group sizes</SelectItem>
                        <SelectItem value="threshold">Only groups larger than</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      min={0}
                      value={settings.staffEmailGroupSize < 0 ? "" : settings.staffEmailGroupSize}
                      onChange={(e) => handleGroupSizeValueChange("staffEmailGroupSize", e.target.value)}
                      disabled={settings.staffEmailGroupSize < 0}
                      placeholder="8"
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">If set to 8, email is sent only when the group size is greater than 8.</p>
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </Card>

          {/* Large Party SMS Handling */}
          <Card className="p-6 card-hover">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-lg font-semibold">Large Party SMS Handling</h3>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="enable-large-party-sms">Handle large parties by SMS</Label>
                  <p className="text-sm text-muted-foreground">
                    When enabled, large-party calls send an SMS to the caller instead of transferring to a human.
                  </p>
                </div>
                <Switch 
                  id="enable-large-party-sms" 
                  checked={isLargePartySmsEnabled}
                  onCheckedChange={(checked) => {
                    setIsLargePartySmsEnabled(checked);
                    if (checked) {
                      setSettings((prev) => ({ ...prev, enableClientSms: true }));
                    }
                  }}
                />
              </div>

              {isLargePartySmsEnabled && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="large-party-template-fr">French SMS Template</Label>
                    <Textarea
                      id="large-party-template-fr"
                      placeholder="Merci! Pour les groupes de plus de {{max_group_size}} personnes, veuillez envoyer un email à exemple@exemple.ca et nous vous confirmerons le tout sous 48h."
                      rows={4}
                      className="resize-none"
                      value={settings.largePartySmsTemplateFr || ""}
                      onChange={(e) => setSettings({ ...settings, largePartySmsTemplateFr: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="large-party-template-en">English SMS Template</Label>
                    <Textarea
                      id="large-party-template-en"
                      placeholder="Thank you! For parties larger than {{max_group_size}}, please send an email to example@example.com and we will confirm the total within 48 hours."
                      rows={4}
                      className="resize-none"
                      value={settings.largePartySmsTemplateEn || ""}
                      onChange={(e) => setSettings({ ...settings, largePartySmsTemplateEn: e.target.value })}
                    />
                  </div>

                  <p className="text-xs text-muted-foreground">
                    You can use these placeholders: {"{{max_group_size}}"} for the maximum group size and {"{{party_size}}"} for the requested party size during the call.
                  </p>
                </>
              )}

              {(chatSettings?.maxGroupSize ?? -1) < 0 && (
                <p className="text-xs text-muted-foreground">
                  Tip: set a maximum group size in Voice Settings to activate this policy.
                </p>
              )}

              <div className="flex justify-end">
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </Card>
        </>
      )}

      {/* SMS History */}
      <Card className="p-6 card-hover">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Clock className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold">SMS History</h3>
            <p className="text-sm text-muted-foreground">
              {totalCount > 0 ? `${totalCount} messages total` : "Most recent messages first"}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => loadSmsLogs()} disabled={logsLoading}>
            {logsLoading ? "Refreshing..." : "Refresh"}
          </Button>
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => handleSort("sentAt")}
                    className="flex items-center gap-2 text-left"
                  >
                    Sent At
                    {renderSortIcon("sentAt")}
                  </button>
                </TableHead>
                <TableHead className="whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => handleSort("toNumber")}
                    className="flex items-center gap-2 text-left"
                  >
                    To Number
                    {renderSortIcon("toNumber")}
                  </button>
                </TableHead>
                <TableHead>
                  <button
                    type="button"
                    onClick={() => handleSort("content")}
                    className="flex items-center gap-2 text-left"
                  >
                    Content
                    {renderSortIcon("content")}
                  </button>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logsLoading ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center py-6">
                    Loading SMS history...
                  </TableCell>
                </TableRow>
              ) : sortedLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center py-6 text-muted-foreground">
                    No SMS messages have been sent yet.
                  </TableCell>
                </TableRow>
              ) : (
                sortedLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="font-mono text-sm">
                      {formatSentAt(log.sentAt)}
                    </TableCell>
                    <TableCell className="font-mono text-sm">{log.toNumber}</TableCell>
                    <TableCell className="text-sm whitespace-pre-wrap break-words">{log.content}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-4">
            <p className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage <= 1 || logsLoading}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage >= totalPages || logsLoading}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
